import { html } from 'htm/preact';
import { useEffect, useRef } from 'react';
import type { LatLngBounds, Layer, LeafletMouseEvent, Map as LeafletMapInstance, Path, Polygon } from 'leaflet';
import { getLeaflet } from '../lib/leaflet';
import { CidadeProps, ConcelhoGeoProps, MapGeoDoc, SedeProps } from '../lib/mapGeo';
import { readPalette } from '../lib/palette';
import { bboxCenterOfMultiPolygon, featureAt, highestBandAt, nearestPointAtLongitude, nearestPointInLinesAtLongitude, pointInMultiPolygon } from '../lib/geometry';
import { PointSelection } from '../lib/selection';

/**
 * O mapa. Leaflet 1.9.4 do vendor (ver lib/leaflet.ts), **sem tile layer**.
 *
 * Sem tiles de propósito, e é a decisão mais consequente deste componente: a
 * política de uso dos tiles do OpenStreetMap proíbe uso comercial pesado, e o tier
 * gratuito dos basemaps da CARTO não cobre um site de notícias no dia em que isto
 * está linkado da homepage. Todas as instâncias de Interactive que já usam Leaflet
 * neste repo o fazem da mesma maneira — Leaflet como renderer de GeoJSON, zero
 * pedidos a terceiros em runtime. O que substitui os rótulos do OSM é a nossa
 * própria geometria: contorno dos países, fronteira, os 18 distritos em linha fina,
 * as 278 sedes de concelho rotuladas, e os selects distrito -> concelho ao lado.
 *
 * `preferCanvas` não é opcional: a camada de concelhos são 278 polígonos com ~18 mil
 * vértices e o renderer SVG do Leaflet criaria 278 nós de DOM — pesado no telefone,
 * que é onde está a maioria dos leitores.
 *
 * O hit-testing é do Leaflet, não nosso: a camada de concelhos é interactiva e cada
 * polígono traz o seu próprio handler. `bubblingMouseEvents: false` é o que impede o
 * clique num concelho de chegar TAMBÉM ao handler do mapa (que trata dos cliques
 * fora de Portugal continental) — sem isso as duas selecções disparavam ao mesmo
 * tempo e a última a escrever ganhava.
 */

// Enquadramento: Ibéria, sem o norte de África que a moldura dos dados inclui (a
// grelha de ocultação vai até 35°N, mas ninguém abre um interactivo do Observador
// para ver a Argélia).
const IBERIA_BOUNDS: [[number, number], [number, number]] = [
    [36.0, -9.8],
    [43.9, 3.6],
];

/**
 * Enquadramento alternativo para caixas em retrato (telefone): Portugal continental
 * mais uma margem a leste que chega para incluir a faixa de totalidade sobre
 * Trás-os-Montes.
 *
 * A Ibéria é uma forma larga (~1,3:1); numa caixa de 374x444 entra pela largura e
 * deixa 170 px de vazio em cima e em baixo. Portugal é alto e estreito — enche a
 * mesma caixa. O leitor continua a poder afastar até à Ibéria inteira (o minZoom é
 * calculado sobre a moldura completa, não sobre este enquadramento).
 */
const PORTUGAL_BOUNDS: [[number, number], [number, number]] = [
    [36.8, -9.9],
    [42.6, -4.8],
];

/**
 * Os dois arquipélagos, para os botões de enquadramento. São factos geográficos
 * fixos; não vale a pena derivá-los dos polígonos em runtime.
 */
const MADEIRA_BOUNDS: [[number, number], [number, number]] = [
    [32.55, -17.35],
    [33.2, -16.2],
];
const ACORES_BOUNDS: [[number, number], [number, number]] = [
    [36.85, -31.4],
    [39.8, -24.9],
];

/**
 * O botão "Mundo": enquadra a moldura completa de `geo.meta.worldFrame` (Islândia a
 * norte, dentro de África a sul — ver DATA.md). Não é a vista inicial (ver
 * `frameConcelho`/o `fitBounds` no rAF de arranque) — só o atalho para quem quiser
 * ver a extensão da linha do limite de visibilidade e a terra do mundo por baixo.
 */
function worldBounds(geo: MapGeoDoc): [[number, number], [number, number]] {
    const f = geo.meta.worldFrame;
    return [
        [f.latMin, f.lonMin],
        [f.latMax, f.lonMax],
    ];
}

const MAX_ZOOM = 11;
/** Abaixo deste zoom os 278 pontos de sede tornam-se ruído; só as 18 sedes de
 *  distrito ficam visíveis desde o início. */
const SEDE_ZOOM = 8;
/**
 * Abaixo deste zoom nem os rótulos das 18 sedes de distrito são desenhados.
 *
 * Não é uma preferência estética: a 390 px de largura a Ibéria entra a zoom ~5,3 e
 * os 18 rótulos empilham-se uns sobre os outros ("Viana do Castelo/Braga/Vila Real"
 * ilegíveis). Rótulos sobrepostos são pior do que rótulo nenhum — e quem estiver no
 * telefone tem os `<select>` logo abaixo do mapa, além de poder aproximar.
 */
const CAPITAL_LABEL_ZOOM = 6;

const CAPITAL_TOOLTIP_OPTIONS = { permanent: true, direction: 'right' as const, offset: [4, 0] as [number, number], className: 'eclipse-map-label eclipse-map-label--capital' };

/** Cidades espanholas: as de `tier: 1` acompanham os rótulos de distrito; as de
 *  `tier: 2` só ao aproximar, senão são três famílias de rótulos a disputar os
 *  mesmos pixéis (o empilhamento a 390 px voltaria). */
const CITY_TIER2_ZOOM = 7.5;

/**
 * Longitude de referência para ancorar os rótulos de percentagem das bandas — ver
 * `nearestPointAtLongitude`. -9,8° fica a oeste de todo o litoral português (a
 * costa varia entre ~-8,8° no Minho e ~-9,5° no Cabo da Roca) e as 8 bandas têm
 * todas um vértice próximo — a mais alta (99,5%) já alcança -12,2°, bem mais a
 * oeste. O resultado é uma coluna ao largo da costa, sul (90%) a norte (99,5%).
 */
const BAND_LABEL_LON = -9.8;

/** Longitude de referência para ancorar o rótulo do limite de visibilidade — cai
 *  perto do meio dos troços que o gerador já entrega limpos (ver DATA.md). */
const VISIBILITY_LABEL_LON = -8;

/**
 * Enquadrar um concelho. Partilhado entre o arranque (deep link `#concelho=`) e a
 * mudança de escolha, para os dois não poderem divergir.
 *
 * Nas ilhas o enquadramento é mais largo e o zoom limitado: o concelho do Funchal
 * sozinho a zoom 10,5 é uma mancha sem contexto — a ilha inteira diz muito mais a
 * quem acabou de ser levado para o meio do Atlântico.
 */
function frameConcelho(map: LeafletMapInstance, bounds: LatLngBounds, isIsland: boolean, animate: boolean): void {
    map.fitBounds(bounds.pad(isIsland ? 1.5 : 1.2), { maxZoom: isIsland ? 10 : MAX_ZOOM, animate });
}

interface Props {
    geo: MapGeoDoc;
    selectedCode: string | null;
    onSelectConcelho: (code: string) => void;
    onSelectPoint: (point: PointSelection) => void;
}

export function EclipseLeafletMap({ geo, selectedCode, onSelectConcelho, onSelectPoint }: Props) {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<LeafletMapInstance | null>(null);
    /** dtmnfr -> o Path do concelho, para poder repintar o selecionado sem redesenhar
     *  a camada toda (repintar 278 polígonos a cada escolha dava um flash visível). */
    const concelhoPathsRef = useRef<Record<string, Path>>({});
    const sedeLayersRef = useRef<{ layer: Layer; capital: boolean }[]>([]);
    const cityLayersRef = useRef<{ layer: Layer; tier: number }[]>([]);
    /** dtmnfr -> region, só para decidir a folga do enquadramento (uma ilha pequena
     *  precisa de muito mais margem que um concelho do continente). */
    const concelhoRegionsRef = useRef<Record<string, string>>({});
    /** Guardado num ref para os handlers do Leaflet (registados uma só vez) verem
     *  sempre o callback actual sem terem de ser re-registados a cada render. */
    const handlersRef = useRef({ onSelectConcelho, onSelectPoint });
    handlersRef.current = { onSelectConcelho, onSelectPoint };
    /** Também num ref: os handlers de mouseover/mouseout são registados uma única vez
     *  na construção e precisam de saber qual é o concelho escolhido *agora*, sem
     *  serem re-registados a cada escolha. */
    const selectedCodeRef = useRef<string | null>(selectedCode);

    // ---------------------------------------------------------------- construção
    useEffect(() => {
        const L = getLeaflet();
        const container = containerRef.current;
        if (!L || !container || mapRef.current) return;

        const palette = readPalette(container, geo.thresholds.length);

        const map = L.map(container, {
            preferCanvas: true,
            // Dentro de um artigo, a roda do rato tem de continuar a fazer scroll à
            // página — daí os botões de zoom explícitos serem a única forma no desktop.
            scrollWheelZoom: false,
            zoomControl: true,
            zoomSnap: 0.25,
            maxZoom: MAX_ZOOM,
            maxBoundsViscosity: 1,
            attributionControl: true,
        });
        mapRef.current = map;

        map.attributionControl.setPrefix('<a href="https://leafletjs.com" rel="noopener">Leaflet</a>');
        map.attributionControl.addAttribution('Ocultação: cálculo do Observador (astronomy-engine) · Fronteiras: Natural Earth, CAOP');

        /**
         * Cada camada no seu pane com um canvas próprio: é o que dá controlo
         * determinístico da ordem de pintura (a ordem de inserção dentro de um mesmo
         * renderer não é fiável quando se adicionam camadas em momentos diferentes).
         *
         * O preço, e é uma armadilha silenciosa: **cada renderer de canvas cria um
         * `<canvas>` do tamanho do mapa inteiro**, e num empilhamento destes só o de
         * cima recebe eventos do rato. Com todos os panes clicáveis, os cliques
         * morriam no canvas dos pontos (o mais alto) e a camada de concelhos nunca era
         * atingida — o mapa parecia simplesmente não responder ao clique.
         *
         * Por isso: `pointer-events: none` em todos os panes excepto o de hit. Só uma
         * camada precisa de receber cliques, e é a dos 278 concelhos.
         */
        const pane = (name: string, zIndex: number, { interactive = false } = {}) => {
            map.createPane(name);
            const el = map.getPane(name);
            if (el) {
                el.style.zIndex = String(zIndex);
                if (!interactive) el.style.pointerEvents = 'none';
            }
            return L.canvas({ pane: name });
        };
        const landRenderer = pane('eclipse-land', 200);
        const bandsRenderer = pane('eclipse-bands', 210);
        const linesRenderer = pane('eclipse-lines', 230);
        const hitRenderer = pane('eclipse-hit', 240, { interactive: true });
        const pointsRenderer = pane('eclipse-points', 250);

        // 0. terra do mundo: silhueta única (sem fronteiras), só contexto para a área
        //    nova — Islândia, o resto da Europa, Marrocos — que não tem geometria
        //    detalhada própria. No MESMO pane que a terra detalhada e adicionada
        //    ANTES dela de propósito: dentro de um pane de canvas a ordem de adição é
        //    a ordem de pintura, por isso a Ibéria detalhada (passo 1) fica por cima
        //    e substitui esta silhueta exactamente onde as duas se sobrepõem.
        L.geoJSON(geo.worldLand, {
            interactive: false,
            style: { renderer: landRenderer, stroke: false, fillColor: palette.worldLand, fillOpacity: 1 },
        }).addTo(map);

        // 1. terra da Ibéria por cima da do mundo: é o que faz a costa continuar a
        //    ler-se depois de as bandas (semi-transparentes) passarem por cima do mar
        //    e da terra.
        L.geoJSON(geo.countries, {
            interactive: false,
            style: { renderer: landRenderer, stroke: false, fillColor: palette.land, fillOpacity: 1 },
        }).addTo(map);

        // 2. bandas, por ordem ascendente — cada uma é "ocultação >= threshold", uma
        //    área menor desenhada por cima da anterior. Contorno leve na própria cor da
        //    banda (não branco nem cinzento — "mais leve" é peso de linha, não a perda
        //    da cor) para a fronteira entre bandas se ler como linha, não só como
        //    salto de tom; e um rótulo de percentagem ancorado a um ponto real da
        //    própria linha (`nearestPointAtLatitude`), para a % não viver só na
        //    legenda do canto.
        for (let i = 0; i < geo.bands.features.length; i++) {
            const f = geo.bands.features[i];
            const color = palette.bands[i];
            L.geoJSON(f, {
                interactive: false,
                style: { renderer: bandsRenderer, stroke: true, color, weight: 1, opacity: 0.55, fillColor: color, fillOpacity: palette.bandOpacity },
            }).addTo(map);

            // O texto do rótulo NÃO leva a cor da banda, de propósito: as bandas mais
            // altas (97%+) são tons escuros sobre um fundo já escuro — texto na mesma
            // cor da banda ficava ilegível exactamente onde a informação mais importa
            // (perto da totalidade). O halo de `.eclipse-map-label` já garante leitura
            // sobre qualquer banda; é a LINHA, não o texto, que fica na cor da banda.
            //
            // Ancorado a oeste da costa (`nearestPointAtLongitude`), não a uma
            // latitude fixa: pedido explícito para os 8 rótulos ficarem ao largo,
            // numa coluna. "Vertical" aqui é a DISPOSIÇÃO da coluna (sul a norte),
            // não a orientação do texto — o texto fica horizontal, esquerda para
            // direita, que é sempre mais legível do que rodado.
            const anchor = nearestPointAtLongitude(f.geometry, BAND_LABEL_LON);
            if (anchor) {
                const [lon, lat] = anchor;
                L.circleMarker([lat, lon], { renderer: pointsRenderer, radius: 0, opacity: 0, fillOpacity: 0, interactive: false })
                    .bindTooltip(f.properties.label, {
                        permanent: true,
                        direction: 'center',
                        className: 'eclipse-map-label eclipse-map-label--band',
                    })
                    .addTo(map);
            }
        }

        // 3. a faixa de totalidade tem tratamento próprio — nunca confundível com a
        //    banda mais alta de parcial (99,5%), que é a razão de ter contorno tracejado.
        if (geo.totality.features.length) {
            L.geoJSON(geo.totality, {
                interactive: false,
                style: { renderer: linesRenderer, color: palette.totalityLine, weight: 1.2, dashArray: '5 4', fillColor: palette.totality, fillOpacity: 0.55 },
            }).addTo(map);
        }

        // 3b. limite de visibilidade: onde o eclipse deixa de acontecer com o Sol no
        //     céu — o pôr-do-sol do dia, não uma banda de ocultação. Só linha, sem
        //     preenchimento algum: a área "visível" cobre quase toda a moldura (ver
        //     DATA.md), pintá-la marcaria o mapa inteiro em vez de assinalar só a
        //     fronteira. Tracejado mais fino que a totalidade, para as duas linhas
        //     tracejadas do mapa nunca se confundirem.
        if (geo.visibilityLimit.features.length) {
            L.geoJSON(geo.visibilityLimit, {
                interactive: false,
                style: { renderer: linesRenderer, color: palette.visibilityLine, weight: 1, opacity: 0.75, dashArray: '2 5', fill: false },
            }).addTo(map);

            const anchor = nearestPointInLinesAtLongitude(geo.visibilityLimit, VISIBILITY_LABEL_LON);
            if (anchor) {
                const [lon, lat] = anchor;
                L.circleMarker([lat, lon], { renderer: pointsRenderer, radius: 0, opacity: 0, fillOpacity: 0, interactive: false })
                    .bindTooltip('Limite de visibilidade do eclipse (pôr-do-sol)', {
                        permanent: true,
                        direction: 'center',
                        className: 'eclipse-map-label eclipse-map-label--visibility',
                    })
                    .addTo(map);
            }
        }

        // 4. linhas: distritos finos por baixo, contorno de país por cima.
        L.geoJSON(geo.distritos, {
            interactive: false,
            style: { renderer: linesRenderer, color: palette.distritoLine, weight: 0.6, opacity: 0.8, fill: false },
        }).addTo(map);

        L.geoJSON(geo.countries, {
            interactive: false,
            style: { renderer: linesRenderer, color: palette.coastline, weight: 1, opacity: 0.9, fill: false },
        }).addTo(map);

        // 4b. nomes dos países de contexto — nunca Portugal/Espanha, que já têm
        //     distritos, sedes e cidades a rotulá-los; um "PORTUGAL"/"ESPANHA" por
        //     cima disso seria só ruído. É o que faz a Bélgica/Luxemburgo/Suíça
        //     (acrescentadas para dar mais contexto a leste) e Marrocos/Argélia
        //     lerem-se como território, não como manchas sem nome.
        for (const f of geo.countries.features) {
            if (f.properties.iso3 === 'PRT' || f.properties.iso3 === 'ESP') continue;
            const [lon, lat] = bboxCenterOfMultiPolygon(f.geometry);
            L.circleMarker([lat, lon], { renderer: pointsRenderer, radius: 0, opacity: 0, fillOpacity: 0, interactive: false }).bindTooltip(f.properties.nome.toUpperCase(), { permanent: true, direction: 'center', className: 'eclipse-map-label eclipse-map-label--country' }).addTo(map);
        }

        // 5. concelhos: transparentes, existem só para serem clicáveis e para dar o
        //    realce ao passar o cursor. É esta camada que responde "qual é o meu".
        const concelhoPaths: Record<string, Path> = {};
        const regions: Record<string, string> = {};
        L.geoJSON(geo.concelhos, {
            bubblingMouseEvents: false,
            style: { renderer: hitRenderer, stroke: false, fillOpacity: 0, fillColor: palette.concelhoHover },
            onEachFeature: (feature, layer) => {
                const props = feature.properties as ConcelhoGeoProps;
                const path = layer as Path;
                concelhoPaths[props.dtmnfr] = path;
                regions[props.dtmnfr] = props.region;

                layer.bindTooltip(`${props.nome} — ${String(props.obscurationPct).replace('.', ',')}%`, { sticky: true, className: 'eclipse-map-tip' });

                layer.on({
                    mouseover: () => {
                        if (props.dtmnfr === selectedCodeRef.current) return;
                        path.setStyle({ stroke: true, color: palette.concelhoHover, weight: 1, fillOpacity: 0.22 });
                    },
                    mouseout: () => {
                        if (props.dtmnfr === selectedCodeRef.current) return;
                        path.setStyle({ stroke: false, fillOpacity: 0 });
                    },
                    click: () => handlersRef.current.onSelectConcelho(props.dtmnfr),
                });
            },
        }).addTo(map);
        concelhoPathsRef.current = concelhoPaths;
        concelhoRegionsRef.current = regions;

        // 6. sedes de concelho. As 18 de distrito ficam sempre rotuladas — são a única
        //    referência de orientação que temos sem os rótulos de um basemap.
        //
        //    `interactive: false` porque o pane dos pontos não recebe eventos (ver
        //    pane() acima): o clique atravessa até ao polígono do concelho, que dá a
        //    mesma resposta e uma área muito maior para acertar. Um ponto de 2 px de
        //    raio nunca seria um bom alvo, sobretudo no telefone.
        const sedeLayers: { layer: Layer; capital: boolean }[] = [];
        const capitalLabels: Record<number, string> = {};
        L.geoJSON(geo.sedes, {
            pointToLayer: (feature, latlng) => {
                const props = feature.properties as SedeProps;
                const marker = L.circleMarker(latlng, {
                    renderer: pointsRenderer,
                    radius: props.capital ? 3.2 : 2,
                    color: palette.sede,
                    weight: props.capital ? 1.2 : 0.8,
                    fillColor: palette.sede,
                    fillOpacity: props.capital ? 1 : 0.7,
                    interactive: false,
                });
                // Rótulo permanente para as sedes de distrito **e** para todas as
                // insulares: as ilhas são 30 pontos a 1000 km da Península, nunca
                // disputam pixéis com os rótulos do continente, e sem nome uma mancha
                // no meio do Atlântico não diz nada a ninguém.
                const labelled = !!props.capital || props.region !== 'continente';
                if (labelled) {
                    // O texto fica guardado à parte porque o rótulo é desligado e
                    // religado por zoom em applyZoomDensity().
                    capitalLabels[L.Util.stamp(marker)] = props.nome;
                    marker.bindTooltip(props.nome, CAPITAL_TOOLTIP_OPTIONS);
                }
                sedeLayers.push({ layer: marker, capital: labelled });
                return marker;
            },
        }).addTo(map);
        sedeLayersRef.current = sedeLayers;

        // 7. cidades espanholas: só rótulo + ponto, `interactive: false`. Não são
        //    seleccionáveis porque só Portugal tem cálculo por concelho — mas mostrar a
        //    ocultação delas é o que torna legível que a totalidade passa a norte, em
        //    Espanha, e não em Portugal.
        const cityLayers: { layer: Layer; tier: number }[] = [];
        L.geoJSON(geo.cidades, {
            pointToLayer: (feature, latlng) => {
                const props = feature.properties as CidadeProps;
                const isTotal = props.eclipseKind === 'total';
                const marker = L.circleMarker(latlng, {
                    renderer: pointsRenderer,
                    radius: props.tier === 1 ? 2.8 : 2,
                    color: isTotal ? palette.totalityLine : palette.city,
                    weight: 1,
                    fillColor: isTotal ? palette.totalityLine : palette.city,
                    fillOpacity: 0.85,
                    interactive: false,
                });
                marker.bindTooltip(`${props.nome} <span>${isTotal ? '100%' : props.obscurationLabel}</span>`, {
                    permanent: true,
                    direction: 'right',
                    offset: [4, 0],
                    className: `eclipse-map-label eclipse-map-label--city${isTotal ? ' eclipse-map-label--city-total' : ''}`,
                });
                cityLayers.push({ layer: marker, tier: props.tier });
                return marker;
            },
        }).addTo(map);
        cityLayersRef.current = cityLayers;

        // 8. Montesinho: anotação permanente, não depende de escolha nenhuma. É a
        //    história da peça — o único pedaço de Portugal onde há totalidade.
        if (geo.montesinho) {
            const [lon, lat] = geo.montesinho.geometry.coordinates;
            // A duração publicada, não a mediana calculada — ver displayDurationSeconds
            // em MontesinhoProps. Sem isto o rótulo dizia 20s enquanto o texto do
            // artigo, a poucos centímetros, dizia 26.
            const seconds = geo.montesinho.properties.displayDurationSeconds ?? geo.montesinho.properties.medianDurationSeconds;
            L.circleMarker([lat, lon], {
                renderer: pointsRenderer,
                radius: 5,
                color: palette.totalityLine,
                weight: 1.6,
                fillColor: palette.totality,
                fillOpacity: 1,
                interactive: false, // como as sedes: o clique atravessa até ao concelho
            })
                // Rótulo acima e à direita: a sede de Bragança fica a sudoeste deste
                // ponto e os dois textos sobrepunham-se com o offset horizontal.
                .bindTooltip(`Totalidade em Portugal (~${seconds}s)<br>${geo.montesinho.properties.areaLabel}`, {
                    permanent: true,
                    direction: 'right',
                    offset: [8, -22],
                    className: 'eclipse-map-label eclipse-map-label--totality',
                })
                .addTo(map);
        }

        // Gancho de QA: a instância do Leaflet no próprio elemento do mapa, para poder
        // ser inspeccionada da consola do browser (`document.querySelector('.eclipse-map')
        // ._eclipseMap`) sem ter de a expor num global. Mesmo espírito dos parâmetros
        // ?eclipseNow= / ?eclipseState= do banner da homepage.
        (container as HTMLElement & { _eclipseMap?: LeafletMapInstance })._eclipseMap = map;

        // Clique fora dos concelhos (Espanha, França, mar). Só chega aqui porque a
        // camada de concelhos tem bubblingMouseEvents: false.
        map.on('click', (evt: LeafletMouseEvent) => {
            const { lat, lng } = evt.latlng;
            const band = highestBandAt(lng, lat, geo.bands);
            const country = featureAt(lng, lat, geo.countries);
            const inTotality = geo.totality.features.some(f => pointInMultiPolygon(lng, lat, f.geometry));
            handlersRef.current.onSelectPoint({
                lat,
                lon: lng,
                thresholdPct: band?.threshold ?? null,
                thresholdLabel: band?.label ?? null,
                countryName: country?.properties.nome ?? null,
                inTotality,
            });
        });

        // Densidade por zoom: 278 sedes à escala peninsular são ruído, e num ecrã
        // estreito até os 18 rótulos de distrito se sobrepõem (ver CAPITAL_LABEL_ZOOM).
        const applyZoomDensity = () => {
            const zoom = map.getZoom();
            const showAll = zoom >= SEDE_ZOOM;
            const showLabels = zoom >= CAPITAL_LABEL_ZOOM;
            for (const { layer, capital } of sedeLayersRef.current) {
                if (capital) {
                    // O tooltip permanente é a única coisa que se esconde — o ponto da
                    // sede de distrito fica sempre, é referência de orientação.
                    const tooltip = layer.getTooltip();
                    if (showLabels && !tooltip) layer.bindTooltip(capitalLabels[L.Util.stamp(layer)] ?? '', CAPITAL_TOOLTIP_OPTIONS);
                    else if (!showLabels && tooltip) layer.unbindTooltip();
                    continue;
                }
                const isOnMap = map.hasLayer(layer);
                if (showAll && !isOnMap) layer.addTo(map);
                else if (!showAll && isOnMap) map.removeLayer(layer);
            }
            for (const { layer, tier } of cityLayersRef.current) {
                const show = tier === 1 ? showLabels : zoom >= CITY_TIER2_ZOOM;
                const isOnMap = map.hasLayer(layer);
                if (show && !isOnMap) layer.addTo(map);
                else if (!show && isOnMap) map.removeLayer(layer);
            }
        };
        map.on('zoomend', applyZoomDensity);

        // A moldura da geometria (`meta.frame` — lon -32°/4,6°, lat -8°/68°: Islândia a
        // norte, dentro de África a sul) manda no limite de deslocação **e** no zoom
        // mínimo. Isto é uma inversão face à primeira versão, que calculava o mínimo
        // sobre a Ibéria: aí o pan funcionava, mas o leitor nunca via que havia algo lá
        // — ao afastar continuava a ver só a Península. Com o mínimo na moldura
        // completa, afastar mostra Portugal, a Madeira, os Açores, a Islândia e a linha
        // do limite de visibilidade ao mesmo tempo (o botão "Mundo" salta lá direito),
        // e a partir daí o arrastar faz sentido. O vazio atlântico entre eles é o
        // preço, e é a geografia real.
        const frame = L.latLngBounds([geo.meta.frame.latMin, geo.meta.frame.lonMin], [geo.meta.frame.latMax, geo.meta.frame.lonMax]);
        map.setMaxBounds(frame.pad(0.05));

        // Vista inicial provisória, só para o mapa ter estado válido enquanto as
        // camadas são adicionadas. O enquadramento a sério é o do requestAnimationFrame
        // mais abaixo, depois de o container ter dimensão.
        map.fitBounds(IBERIA_BOUNDS);

        /**
         * Recalcular o zoom mínimo para o tamanho actual do container.
         *
         * Sem tiles, afastar para além do zoom que enquadra a moldura só mostra vazio e
         * deixa a Ibéria a flutuar dentro da caixa — daí trancar aí o mínimo. E o zoom
         * que enquadra a moldura depende da largura disponível, por isso isto tem de
         * correr outra vez a cada resize (breakpoint, rotação, anúncios a assentar).
         */
        const refreshLimits = () => {
            map.setMinZoom(0); // destrancar, senão o cálculo fica limitado pelo mínimo anterior
            map.invalidateSize();
            map.setMinZoom(map.getBoundsZoom(frame));
            applyZoomDensity();
        };

        /**
         * O enquadramento inicial. **Uma só vez**, e depois de `invalidateSize()`.
         *
         * Depois de `invalidateSize()` porque o mapa monta dentro de uma árvore Preact
         * que só renderiza depois do fetch, num container de grid com altura em
         * percentagem: no instante do mount o Leaflet mede zero, um `fitBounds` aí
         * calcula um zoom errado, e o `setMinZoom` a seguir trancava-o como mínimo —
         * deixando o mapa preso demasiado aproximado e sem forma de afastar.
         * (Aconteceu: a primeira versão mostrava só o norte de Portugal.)
         *
         * Uma só vez porque reenquadrar a cada resize roubaria ao leitor o zoom e a
         * posição que ele escolheu sempre que um anúncio assentasse mais abaixo na
         * página. Tentar distinguir "movimento do leitor" de "movimento nosso" a partir
         * dos eventos do Leaflet não é fiável — `fitBounds` e `setMinZoom` disparam
         * `zoomstart` como um gesto disparia, e parte disso é assíncrono. A consequência
         * aceite é que a escolha do enquadramento (retrato/paisagem) é feita no arranque
         * e não muda se o telefone rodar; o `minZoom` continua a acompanhar o tamanho,
         * por isso afastar até à Ibéria inteira continua sempre a funcionar.
         */
        const raf = requestAnimationFrame(() => {
            map.setMinZoom(0);
            map.invalidateSize();

            // Um deep link `#concelho=` manda no enquadramento inicial. Sem isto o
            // efeito de selecção (que corre antes deste rAF, quando o container ainda
            // não tem dimensão) fazia o seu panInside e este fitBounds apagava-o a
            // seguir — abrir a página em #concelho=4801 mostrava a Ibéria em vez dos
            // Açores.
            const initialCode = selectedCodeRef.current;
            const initialPath = initialCode ? concelhoPaths[initialCode] : null;
            if (initialPath) {
                frameConcelho(map, (initialPath as Polygon).getBounds(), regions[initialCode!] !== 'continente', false);
            } else {
                // Caixa em retrato (telefone) -> enquadrar Portugal; em paisagem -> a Ibéria.
                const portrait = container.clientHeight > container.clientWidth * 1.1;
                map.fitBounds(portrait ? PORTUGAL_BOUNDS : IBERIA_BOUNDS, { animate: false });
            }
            refreshLimits();
        });

        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => refreshLimits()) : null;
        observer?.observe(container);

        return () => {
            cancelAnimationFrame(raf);
            observer?.disconnect();
            map.remove();
            mapRef.current = null;
            concelhoPathsRef.current = {};
            concelhoRegionsRef.current = {};
            sedeLayersRef.current = [];
            cityLayersRef.current = [];
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [geo]);

    // ------------------------------------------------- realce do concelho escolhido
    useEffect(() => {
        const L = getLeaflet();
        const map = mapRef.current;
        const container = containerRef.current;
        if (!L || !map || !container) return;

        const palette = readPalette(container, geo.thresholds.length);
        const concelhoRegions = concelhoRegionsRef.current;
        const previous = selectedCodeRef.current;
        selectedCodeRef.current = selectedCode;

        if (previous && previous !== selectedCode) {
            concelhoPathsRef.current[previous]?.setStyle({ stroke: false, fillOpacity: 0 });
        }
        if (!selectedCode) return;

        const path = concelhoPathsRef.current[selectedCode];
        if (!path) return; // sem polígono na moldura (não deve acontecer: os 308 entram)
        path.setStyle({ stroke: true, color: palette.concelhoSelected, weight: 1.6, fillOpacity: 0.3, fillColor: palette.concelhoSelected });
        path.bringToFront();

        const bounds = (path as Polygon).getBounds();
        if (!bounds || !bounds.isValid()) return;

        // Se o concelho está fora da vista, enquadrá-lo. É este ramo que torna a
        // Madeira e os Açores alcançáveis: estão a 1000-1500 km da Ibéria, e ninguém
        // os encontraria a arrastar o mapa às cegas pelo Atlântico. Escolher o
        // concelho no seletor é a porta de entrada.
        // Fora da vista -> enquadrar. É este ramo que torna a Madeira e os Açores
        // alcançáveis; estão a 1000-1500 km da Ibéria.
        if (!map.getBounds().contains(bounds)) {
            frameConcelho(map, bounds, concelhoRegions[selectedCode] !== 'continente', true);
            return;
        }

        // Já visível: panInside em vez de setView, para escolher nos selects não
        // saltar o enquadramento sem razão.
        map.panInside(bounds.getCenter(), { padding: [32, 32] });
    }, [selectedCode, geo]);

    // Botões de enquadramento. Sem eles as ilhas eram inalcançáveis na prática:
    // estão a 1000-1500 km da Península e ninguém arrasta o mapa às cegas por cima do
    // Atlântico à procura delas. O zoom mínimo já mostra tudo de uma vez; isto é o
    // atalho directo.
    const goTo = (bounds: [[number, number], [number, number]]) => () => {
        const L = getLeaflet();
        const map = mapRef.current;
        if (!L || !map) return;
        map.fitBounds(bounds, { maxZoom: MAX_ZOOM });
    };

    return html`
        <div class="eclipse-map" ref=${containerRef} role="application" aria-label="Mapa com as zonas de ocultação do eclipse solar de 12 de agosto de 2026. Clique num concelho para ver as horas locais."></div>
        <div class="eclipse-map-frames">
            <span class="eclipse-map-frames__label">Ver</span>
            <button type="button" onClick=${goTo(IBERIA_BOUNDS)}>Continente</button>
            <button type="button" onClick=${goTo(MADEIRA_BOUNDS)}>Madeira</button>
            <button type="button" onClick=${goTo(ACORES_BOUNDS)}>Açores</button>
            <button type="button" onClick=${goTo(worldBounds(geo))}>Mundo</button>
        </div>
    `;
}
